/**
 * Desktop Form Responses Container
 * 
 * Complete responsive form responses view for desktop
 * Handles table, detail drawer, pagination, bulk delete, and data fetching
 */

import React, { useState, useCallback } from 'react';
import { Download } from 'lucide-react';
import { useFormResponses, useFormResponseDetail, useTablePagination } from './hooks/useFormResponses';
import DesktopFormResponsesTable from './table/DesktopFormResponsesTable';
import { DesktopFormResponseDetailDrawer } from './detail';
import { useAuth } from '../../context/AuthContext';
import { deleteFormResponses } from '../../firebase/formResponse.service';
import { exportFormResponsesCsv } from './utils/exportCsv';
import Toast from '../Toast/Toast';

const DesktopFormResponsesContainer = ({
    propertyId = null, // If provided, filter by property
    columns = null,
    fieldSchema = null,
    title = 'Form Responses',
    subtitle = 'All submissions from website and property pages.',
    onResponseSelected = null,
    propertiesMap = {},
    portfolioName = '',
}) => {
    const { user, loading: authLoading } = useAuth();
    const ownerId = user?.uid;

    console.log('[DesktopFormResponsesContainer] Auth state:', {
        user: !!user,
        authLoading,
        ownerId: ownerId,
        userEmail: user?.email,
    });

    // Fetch responses
    const { responses, loading, error } = useFormResponses(ownerId, {
        propertyId,
        type: propertyId ? 'property' : 'owner',
    });

    console.log('[DesktopFormResponsesContainer] Data fetch state:', {
        loading,
        error,
        responsesCount: responses.length,
        first: responses[0] ? { id: responses[0].id, name: responses[0].responses?.firstName || responses[0].responses?.name } : null,
    });

    // Pagination and sorting
    const {
        paginatedItems,
        page,
        pageSize,
        totalPages,
        totalItems,
        nextPage,
        prevPage,
        setSortBy,
        sortBy,
        sortOrder,
    } = useTablePagination(responses, { pageSize: 10 });

    // Detail view management
    const detail = useFormResponseDetail();

    // Selection state for bulk/single delete
    const [selectedIds, setSelectedIds] = useState([]);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [toast, setToast] = useState(null);

    // Reset selection when page changes
    const handleNextPage = useCallback(() => {
        setSelectedIds([]);
        nextPage();
    }, [nextPage]);
    const handlePrevPage = useCallback(() => {
        setSelectedIds([]);
        prevPage();
    }, [prevPage]);

    // Bulk delete handler
    const handleBulkDeleteRequest = useCallback(() => {
        if (selectedIds.length === 0) return;
        setDeleteConfirm({ type: 'bulk', ids: [...selectedIds] });
    }, [selectedIds]);

    // Single delete handler
    const handleSingleDeleteRequest = useCallback((id) => {
        setDeleteConfirm({ type: 'single', id });
    }, []);

    const handleConfirmDelete = useCallback(async () => {
        if (!deleteConfirm) return;
        const idsToDelete = deleteConfirm.type === 'bulk' ? deleteConfirm.ids : [deleteConfirm.id];
        setIsDeleting(true);
        try {
            const result = await deleteFormResponses(idsToDelete);
            if (result.success) {
                setToast({ type: 'success', message: `Deleted ${result.deletedCount} response${result.deletedCount > 1 ? 's' : ''}` });
                if (deleteConfirm.type === 'bulk') setSelectedIds([]);
            } else {
                setToast({ type: 'error', message: result.error || 'Failed to delete' });
            }
        } catch (err) {
            setToast({ type: 'error', message: err.message || 'Failed to delete' });
        } finally {
            setDeleteConfirm(null);
            setIsDeleting(false);
        }
    }, [deleteConfirm]);

    const handleDismissToast = useCallback(() => setToast(null), []);

    // Create a map of raw responses by ID for detail view
    const rawResponseMap = React.useMemo(() => {
        return responses.reduce((map, response) => {
            map[response.id] = response;
            return map;
        }, {});
    }, [responses]);

    // Handle row click - pass the RAW response, not the formatted one
    const handleRowClick = (formattedResponse) => {
        const rawResponse = rawResponseMap[formattedResponse.id];
        detail.open(rawResponse || formattedResponse);
        onResponseSelected?.(formattedResponse);
    };

    // Export CSV — uses ALL responses (not just the current page)
    const handleExportCSV = useCallback(() => {
        const result = exportFormResponsesCsv(responses, { propertiesMap, portfolioName });
        if (!result.success) {
            setToast({ type: 'error', message: 'No form responses to export' });
        } else {
            setToast({ type: 'success', message: `Exported ${result.count} response${result.count > 1 ? 's' : ''} to CSV` });
        }
    }, [responses, propertiesMap, portfolioName]);

    // Default columns if not provided
    const defaultColumns = [
        { key: 'name', label: 'Contact Name', width: '200px', sortable: true },
        { key: 'email', label: 'Email', width: '220px', sortable: true },
        { key: 'source', label: 'Source', width: '150px', sortable: true },
        { key: 'submittedAtFormatted', label: 'Submitted', width: '150px', sortable: true },
    ];

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-lg font-semibold text-notion-text">{title}</h2>
                    <p className="text-xs text-stone-400 mt-1">{subtitle}</p>
                </div>
                <button
                    onClick={handleExportCSV}
                    className="flex items-center gap-2 px-3 py-1.5 border border-notion-border rounded text-xs font-medium text-stone-600 hover:bg-notion-sidebar transition"
                >
                    <Download className="w-3.5 h-3.5" /> Export CSV
                </button>
            </div>

            {/* Auth Loading State */}
            {authLoading && (
                <div className="bg-white border border-notion-border rounded-lg overflow-hidden">
                    <div className="p-8 text-center">
                        <div className="inline-block">
                            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-stone-700 mx-auto mb-3"></div>
                            <p className="text-sm text-stone-600">Initializing...</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Table */}
            {!authLoading && (
                <DesktopFormResponsesTable
                    responses={paginatedItems}
                    columns={columns || defaultColumns}
                    loading={loading}
                    error={error}
                    onRowClick={handleRowClick}
                    page={page}
                    pageSize={pageSize}
                    totalItems={totalItems}
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSort={setSortBy}
                    onNextPage={handleNextPage}
                    onPrevPage={handlePrevPage}
                    propertiesMap={propertiesMap}
                    portfolioName={portfolioName}
                    selectedIds={selectedIds}
                    onSelectionChange={setSelectedIds}
                    onBulkDelete={handleBulkDeleteRequest}
                    onSingleDelete={handleSingleDeleteRequest}
                />
            )}

            {/* Detail Drawer */}
            {!authLoading && (
                <DesktopFormResponseDetailDrawer
                    response={detail.response}
                    isOpen={detail.isOpen}
                    onClose={detail.close}
                    fieldSchema={fieldSchema}
                />
            )}

            {/* Delete Confirmation Modal */}
            {deleteConfirm && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6 space-y-4">
                        <h3 className="text-sm font-semibold text-stone-900">
                            {deleteConfirm.type === 'bulk'
                                ? `Delete ${deleteConfirm.ids.length} response${deleteConfirm.ids.length > 1 ? 's' : ''}?`
                                : 'Delete this response?'}
                        </h3>
                        <p className="text-xs text-stone-500">This action cannot be undone.</p>
                        <div className="flex justify-end gap-2 pt-2">
                            <button
                                onClick={() => setDeleteConfirm(null)}
                                disabled={isDeleting}
                                className="px-3 py-1.5 text-xs font-medium text-stone-600 border border-notion-border rounded hover:bg-stone-50 transition disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleConfirmDelete}
                                disabled={isDeleting}
                                className="px-3 py-1.5 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded transition disabled:opacity-50"
                            >
                                {isDeleting ? 'Deleting…' : 'Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Toast notification */}
            {toast && <Toast message={toast.message} type={toast.type} onClose={handleDismissToast} />}
        </div>
    );
};

export default DesktopFormResponsesContainer;
